import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import { useAuth } from '../../contexts/AuthContext';

const AuthModal = ({ isOpen, onClose, initialMode = 'signin' }) => {
  const navigate = useNavigate();
  const { signIn, signUp, resetPassword, verifyEmail, resendVerification } = useAuth();
  const [mode, setMode] = useState(initialMode); // 'signin', 'signup', 'forgot', 'verify'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    phone: '',
    verificationCode: ''
  });

  // Reset mode when modal opens with new initialMode
  React.useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setError(null);
      setSuccess(null);
    }
  }, [isOpen, initialMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const validateForm = () => {
    if (!formData.email) {
      setError('Email is required');
      return false;
    }
    if (!formData.email.includes('@')) {
      setError('Please enter a valid email');
      return false;
    }
    
    if (mode !== 'forgot' && mode !== 'verify') {
      if (!formData.password) {
        setError('Password is required');
        return false;
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return false;
      }
    }
    
    if (mode === 'signup') {
      if (!formData.fullName) {
        setError('Full name is required');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }

    if (mode === 'verify') {
      if (!formData.verificationCode || formData.verificationCode.trim().length < 4) {
        setError('Enter the verification code from your email');
        return false;
      }
    }
    
    return true;
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('[Auth] Attempting sign in for:', formData.email);
      const { error: signInError } = await signIn(
        formData.email,
        formData.password
      );
      
      if (signInError) {
        console.error('[Auth] Sign in error:', signInError);
        throw signInError;
      }
      
      console.log('[Auth] Sign in successful');
      setSuccess('Signed in successfully!');
      
      setTimeout(() => {
        onClose();
        resetForm();
        navigate('/caterer-discovery');
      }, 500);
    } catch (err) {
      console.error('[Auth] Sign in failed:', err);
      // If email not confirmed, prompt user to verify
      if (err.message?.includes('Email not confirmed')) {
        setError('Please verify your email first. Enter the code sent to your email.');
        setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
        // Auto-switch to verify mode after a brief delay
        setTimeout(() => {
          setMode('verify');
        }, 1500);
      } else {
        setError(err.message || 'Failed to sign in. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('[Auth] Attempting sign up for:', formData.email);
      const { error: signUpError } = await signUp(
        formData.email,
        formData.password,
        formData.fullName
      );
      
      if (signUpError) {
        console.error('[Auth] Sign up error:', signUpError);
        throw signUpError;
      }
      
      console.log('[Auth] Sign up successful');
      setSuccess('Account created! Please enter the verification code sent to your email.');
      
      setTimeout(() => {
        setMode('verify');
        setSuccess(null);
      }, 2000);
    } catch (err) {
      console.error('[Auth] Sign up failed:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Please enter your email');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('[Auth] Sending password reset to:', formData.email);
      const { error: resetError } = await resetPassword(formData.email);
      
      if (resetError) {
        console.error('[Auth] Password reset error:', resetError);
        throw resetError;
      }
      
      console.log('[Auth] Password reset email sent');
      setSuccess('Password reset email sent! Check your inbox.');
      
      setTimeout(() => {
        setMode('signin');
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('[Auth] Password reset failed:', err);
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      fullName: '',
      phone: '',
      verificationCode: ''
    });
    setError(null);
    setSuccess(null);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal - contained on mobile, scrollable if needed */}
      <div className="relative my-auto bg-card rounded-2xl shadow-2xl w-full max-w-md max-h-[min(90dvh,calc(100vh-2rem))] overflow-y-auto overflow-x-hidden">
        {/* Header */}
        <div className="relative bg-white px-6 py-8 border-b border-border">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <img src="/logo.png" alt="MrsCater" className="h-10 w-auto" />
          </div>
          
          <h2 className="text-2xl font-bold text-foreground">
            {mode === 'signin' && 'Welcome Back'}
            {mode === 'signup' && 'Create Account'}
            {mode === 'forgot' && 'Reset Password'}
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            {mode === 'signin' && 'Sign in to manage your catering orders'}
            {mode === 'signup' && 'Join MrsCater to discover trusted caterers'}
            {mode === 'forgot' && 'Enter your email to reset your password'}
            {mode === 'verify' && 'Enter the verification code sent to your email'}
          </p>
        </div>
        
        {/* Body */}
        <div className="p-6">
          {/* Error Alert */}
          {error && (
            <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg flex items-start gap-2">
              <Icon name="AlertCircle" size={18} className="text-error mt-0.5 flex-shrink-0" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}
          
          {/* Success Alert */}
          {success && (
            <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-lg flex items-start gap-2">
              <Icon name="CheckCircle" size={18} className="text-success mt-0.5 flex-shrink-0" />
              <p className="text-sm text-success">{success}</p>
            </div>
          )}
          
          {/* Sign In Form */}
          {mode === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Email Address
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  iconName="Mail"
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Password
                </label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  iconName="Lock"
                  disabled={loading}
                />
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => switchMode('forgot')}
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              
              <Button
                type="submit"
                variant="default"
                fullWidth
                disabled={loading}
                className="mt-2"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </Button>
              
              <p className="text-center text-sm text-muted-foreground mt-4">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className="text-primary font-medium hover:underline"
                >
                  Sign up
                </button>
              </p>
            </form>
          )}
          
          {/* Sign Up Form */}
          {mode === 'signup' && (
            <form onSubmit={handleSignUp} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Full Name
                </label>
                <Input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  iconName="User"
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Email Address
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  iconName="Mail"
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Password
                </label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  iconName="Lock"
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Confirm Password
                </label>
                <Input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  iconName="Lock"
                  disabled={loading}
                />
              </div>
              
              <Button
                type="submit"
                variant="default"
                fullWidth
                disabled={loading}
                className="mt-2"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Creating account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </Button>
              
              <p className="text-center text-sm text-muted-foreground mt-4">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signin')}
                  className="text-primary font-medium hover:underline"
                >
                  Sign in
                </button>
              </p>
              <div className="flex items-center justify-center gap-3 mt-2">
                <button type="button" className="text-sm text-primary hover:underline" onClick={() => switchMode('verify')}>Already got a code?</button>
                <button type="button" className="text-sm text-primary hover:underline" onClick={async () => {
                  if (!formData.email) { setError('Enter your email to resend verification'); return; }
                  setLoading(true);
                  const { error: resendError } = await resendVerification(formData.email);
                  setLoading(false);
                  if (resendError) {
                    setError(resendError.message || 'Failed to resend verification');
                  } else {
                    setSuccess('Verification email resent! Check your inbox.');
                  }
                }}>Resend verification</button>
              </div>
            </form>
          )}
                    {/* Verify Email Form */}
                    {mode === 'verify' && (
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        if (!validateForm()) return;
                        setLoading(true);
                        setError(null);
                        try {
                          const { error: verifyError } = await verifyEmail(formData.email, formData.verificationCode);
                          if (verifyError) throw verifyError;
                          setSuccess('Email verified! You can now sign in.');
                          setTimeout(() => { setMode('signin'); setSuccess(null); }, 2000);
                        } catch (err) {
                          setError(err.message || 'Verification failed. Please check the code.');
                        } finally {
                          setLoading(false);
                        }
                      }} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
                          <Input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="you@example.com" iconName="Mail" disabled={loading} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">Verification Code</label>
                          <Input type="text" name="verificationCode" value={formData.verificationCode} onChange={handleInputChange} placeholder="Enter code" iconName="ShieldCheck" disabled={loading} />
                        </div>
                        <Button type="submit" variant="default" fullWidth disabled={loading} className="mt-2">
                          {loading ? (
                            <span className="flex items-center gap-2">
                              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                              Verifying...
                            </span>
                          ) : (
                            'Verify Email'
                          )}
                        </Button>
                        <p className="text-center text-sm text-muted-foreground mt-4">
                          Already verified?{' '}
                          <button type="button" onClick={() => switchMode('signin')} className="text-primary font-medium hover:underline">Sign in</button>
                        </p>
                      </form>
                    )}
          
          {/* Forgot Password Form */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Email Address
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  iconName="Mail"
                  disabled={loading}
                />
              </div>
              
              <Button
                type="submit"
                variant="default"
                fullWidth
                disabled={loading}
                className="mt-2"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Sending...
                  </span>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
              
              <p className="text-center text-sm text-muted-foreground mt-4">
                Remember your password?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signin')}
                  className="text-primary font-medium hover:underline"
                >
                  Sign in
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
